import React, { useEffect } from 'react';
import { FiltersMap } from '@wix/bex-core';
import {
  CardsGridVirtual,
  CardsGridVirtualBasePropsWithoutPreset,
} from '../Grid';
import { InfiniteScrollTable, InfiniteScrollTableProps } from '..';
import { observer } from 'mobx-react-lite';
import type { TableGridSwitchState } from '../../state/TableGridSwitchState';
import { TableGridSwitchButton } from '../TableGridSwitchButton';
import { CardsGridPresetProps } from '../Grid/CardsGrid.types';
import type { TableGridSwitchDragAndDrop } from './TableGridSwitchDragAndDrop';
import { InitialLoader } from '../InitialLoader';

export interface TableGridSwitchBaseProps<T, F extends FiltersMap>
  extends Omit<
      CardsGridVirtualBasePropsWithoutPreset<T, F>,
      'state' | 'dragAndDrop' | 'tableGridSwitchButton' | 'sticky'
    >,
    Omit<
      InfiniteScrollTableProps<T, F>,
      'state' | 'dragAndDrop' | 'tableGridSwitchButton' | 'sticky'
    > {
  state: TableGridSwitchState<T, F>;
  /**
   * Drag and drop implementation: `import { TableGridSwitchDragAndDrop } from '@wix/cairo';`
   * > In order to make the drag and drop also re-order the collection after drop, also add [OptimisticActions](/pages/cairo/?path=/story/components-collection-features--optimistic-actions).
   * @external
   */
  dragAndDrop?: typeof TableGridSwitchDragAndDrop;
}

export type TableGridSwitchProps<
  T,
  F extends FiltersMap,
> = CardsGridPresetProps<T, TableGridSwitchBaseProps<T, F>>;

export function _TableGridSwitch<T, F extends FiltersMap = FiltersMap>(
  props: TableGridSwitchProps<T, F>,
) {
  const { state, dragAndDrop } = props;
  const { gridState, infiniteScrollTableState, toolbar } = state;

  const tableGridSwitchButton = (
    <TableGridSwitchButton state={state.viewTypeState} />
  );

  useEffect(() => state.init(), []);

  const {
    initTask: { status },
  } = state;

  const isLoading = status.isIdle || status.isLoading;

  return (
    <>
      {isLoading ? (
        <InitialLoader dataHook="initial-loader" state={toolbar} />
      ) : state.viewType === 'grid' ? (
        <CardsGridVirtual
          {...props}
          tableGridSwitchButton={tableGridSwitchButton}
          state={gridState}
          dragAndDrop={dragAndDrop?.grid}
        />
      ) : (
        <InfiniteScrollTable
          {...props}
          sticky
          tableGridSwitchButton={tableGridSwitchButton}
          state={infiniteScrollTableState}
          dragAndDrop={dragAndDrop?.table}
        />
      )}
    </>
  );
}

export const TableGridSwitch = observer(_TableGridSwitch);
